// FeedbackAdminPage — Ops › Feedback (pen Q67), pure.
//
// Every report people sent (newest first, at most the list ceiling, CL-26),
// filtered in place by kind. A row opens in place and shows the report as
// stored, as coloured JSON with Copy JSON, and the operator's two actions:
// Mark handled (an open report) or Reopen (a handled one), one intent each.
// Which rows are open and which kind is chosen is view state, never stored.
// The sender's words, context and email are untrusted: React text only.

export type { FeedbackStatusFailure };
export { FeedbackAdminPage };

import {
    FEEDBACK_ADMIN_LIST_MAX,
    type FeedbackAdminList,
    type FeedbackAdminReport,
} from "@bilda/app/feedbackAdmin";
import type { Remote } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import type { ExternalError } from "../../app/externalError.js";
import { TableSkeleton } from "../AiUsage/AiUsageStates.js";
import { Button } from "../Button/Button.js";
import { Icon } from "../Icon/Icon.js";
import {
    ContextChips,
    filterByKind,
    JsonBlock,
    KindChips,
    type KindFilter,
    KindTag,
    latest,
    reportJson,
    useFrom,
} from "../Ops/feedbackParts.js";
import {
    type OpsAccess,
    type OpsFrame,
    OpsLoadFailed,
    OpsMenu,
    OpsNoAccess,
    OpsState,
} from "../Ops/OpsMenu.js";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden.js";

type FeedbackAdminRead = Remote.Remote<FeedbackAdminList, ExternalError>;

// The last status change that did not happen, and the server's reason.
type FeedbackStatusFailure = {
    readonly reportId: string;
    readonly reason: string | undefined;
};

type FeedbackAdminPageProps = {
    readonly frame: OpsFrame;
    readonly access: OpsAccess;
    readonly list: FeedbackAdminRead;
    // Opened when the page opens (a link from the Overview).
    readonly initiallyOpen: string | undefined;
    // Reports with a status change on its way.
    readonly pending: ReadonlySet<string>;
    readonly failure: FeedbackStatusFailure | undefined;
    readonly onRetry: () => void;
    readonly onCopy: (json: string) => void;
    readonly onMarkHandled: (report: FeedbackAdminReport) => void;
    readonly onReopen: (report: FeedbackAdminReport) => void;
};

const useFailureLine = (): ((f: FeedbackStatusFailure) => string) => {
    const { t } = useLingui();
    return (f) => {
        switch (f.reason) {
            case "feedback_already_handled":
                return t`Someone already marked this report handled.`;
            case "feedback_already_open":
                return t`This report is already open.`;
            case "feedback_report_not_found":
                return t`This report is no longer stored.`;
            default:
                return t`That did not go through. Nothing was changed.`;
        }
    };
};

const Detail = ({
    report,
    pending,
    failure,
    onCopy,
    onMarkHandled,
    onReopen,
}: {
    readonly report: FeedbackAdminReport;
    readonly pending: boolean;
    readonly failure: FeedbackStatusFailure | undefined;
    readonly onCopy: (json: string) => void;
    readonly onMarkHandled: (report: FeedbackAdminReport) => void;
    readonly onReopen: (report: FeedbackAdminReport) => void;
}) => {
    const { t } = useLingui();
    const failureLine = useFailureLine();
    const json = reportJson(report);
    return (
        <tr className="ops__detail">
            <td />
            <td colSpan={5}>
                <div className="ops__detail-head">
                    <span className="ops__detail-label">{t`The report as stored`}</span>
                    {report.status === "open" ? (
                        <Button
                            variant="secondary"
                            size="sm"
                            disabled={pending}
                            onClick={() => onMarkHandled(report)}
                        >
                            <Icon name="check" size="sm" />
                            {t`Mark handled`}
                        </Button>
                    ) : (
                        <Button
                            variant="secondary"
                            size="sm"
                            disabled={pending}
                            onClick={() => onReopen(report)}
                        >
                            <Icon name="undo" size="sm" />
                            {t`Reopen`}
                        </Button>
                    )}
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onCopy(json)}
                    >
                        <Icon name="copy" size="sm" />
                        {t`Copy JSON`}
                    </Button>
                </div>
                {failure === undefined ? null : (
                    <p className="ops__notice ops__notice--danger" role="alert">
                        <Icon name="alert" size="sm" />
                        {failureLine(failure)}
                    </p>
                )}
                <JsonBlock text={json} />
            </td>
        </tr>
    );
};

const Reports = (props: FeedbackAdminPageProps): ReactNode => {
    const { t, i18n } = useLingui();
    const from = useFrom();
    const [kind, setKind] = useState<KindFilter>("all");
    const [open, setOpen] = useState<ReadonlySet<string>>(
        () =>
            new Set(
                props.initiallyOpen === undefined ? [] : [props.initiallyOpen],
            ),
    );
    const { list } = props;
    if (list._tag === "Remote.Failed")
        return (
            <OpsLoadFailed
                title={t`Feedback could not be loaded`}
                onRetry={props.onRetry}
            />
        );
    const value = latest(list);
    if (value === undefined) return <TableSkeleton />;
    const { reports, unreadable } = value;
    const toggle = (id: string) =>
        setOpen((prev) => {
            const next = new Set(prev);
            if (!next.delete(id)) next.add(id);
            return next;
        });
    const when = new Intl.DateTimeFormat(i18n.locale, {
        dateStyle: "medium",
        timeStyle: "short",
    });
    if (reports.length === 0)
        return (
            <OpsState
                icon="ops-inbox"
                title={t`No feedback yet`}
                body={t`Reports people send with Give feedback and Report this show up here.`}
            />
        );
    return (
        <div className="ops__section">
            <KindChips reports={reports} value={kind} onChange={setKind} />
            <div className="ops__panel">
                <table className="ops__table ops__table--feedback">
                    <thead>
                        <tr>
                            <th scope="col">{t`Sent`}</th>
                            <th scope="col">{t`Kind`}</th>
                            <th scope="col">{t`Text and context`}</th>
                            <th scope="col">{t`Contact`}</th>
                            <th scope="col">{t`From`}</th>
                            <th scope="col">
                                <VisuallyHidden>{t`Open`}</VisuallyHidden>
                            </th>
                        </tr>
                    </thead>
                    {filterByKind(reports, kind).map((r) => {
                        const isOpen = open.has(r.reportId);
                        return (
                            <tbody key={r.reportId}>
                                <tr
                                    className={`ops__row--link${isOpen ? " ops__row--open" : ""}`}
                                    onClick={() => toggle(r.reportId)}
                                >
                                    <td>{when.format(new Date(r.at))}</td>
                                    <td>
                                        <span className="ops__context">
                                            <KindTag kind={r.kind} />
                                            {r.status === "handled" ? (
                                                <span className="ops__tag ops__tag--neutral">
                                                    <Icon
                                                        name="check"
                                                        size="xs"
                                                    />
                                                    {t`Handled`}
                                                </span>
                                            ) : null}
                                        </span>
                                    </td>
                                    <td>
                                        <span className="ops__report-main">
                                            <span className="ops__text">
                                                {r.text}
                                            </span>
                                            <ContextChips context={r.context} />
                                        </span>
                                    </td>
                                    <td>{r.mayContact ? t`Yes` : t`No`}</td>
                                    <td
                                        className={
                                            r.reporterEmail === null
                                                ? "ops__faint"
                                                : undefined
                                        }
                                    >
                                        {from(r)}
                                    </td>
                                    <td>
                                        <button
                                            type="button"
                                            className="ops__row-button"
                                            aria-expanded={isOpen}
                                            aria-label={
                                                isOpen
                                                    ? t`Close report`
                                                    : t`Open report`
                                            }
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggle(r.reportId);
                                            }}
                                        >
                                            <Icon
                                                name={
                                                    isOpen
                                                        ? "chevron-down"
                                                        : "chevron-right"
                                                }
                                                size="sm"
                                            />
                                        </button>
                                    </td>
                                </tr>
                                {isOpen ? (
                                    <Detail
                                        report={r}
                                        pending={props.pending.has(r.reportId)}
                                        failure={
                                            props.failure?.reportId ===
                                            r.reportId
                                                ? props.failure
                                                : undefined
                                        }
                                        onCopy={props.onCopy}
                                        onMarkHandled={props.onMarkHandled}
                                        onReopen={props.onReopen}
                                    />
                                ) : null}
                            </tbody>
                        );
                    })}
                </table>
            </div>
            {reports.length >= FEEDBACK_ADMIN_LIST_MAX ? (
                <p className="ops__foot">{t`Showing the newest ${FEEDBACK_ADMIN_LIST_MAX}.`}</p>
            ) : null}
            {unreadable > 0 ? (
                <p className="ops__foot" role="status">
                    {t`${unreadable} stored reports could not be read and are not shown.`}
                </p>
            ) : null}
        </div>
    );
};

const FeedbackAdminPage = (props: FeedbackAdminPageProps): ReactNode => {
    const { t } = useLingui();
    return (
        <div className="ai-usage">
            <OpsMenu
                frame={props.frame}
                access={props.access}
                current="feedback"
            />
            <main className="ai-usage__main">
                {props.access === "denied" ? (
                    <OpsNoAccess />
                ) : (
                    <>
                        <header className="ai-usage__heading">
                            <h1 className="ops__title">{t`Feedback`}</h1>
                            <p className="ai-usage__lede">
                                {t`What people sent with Give feedback and Report this, newest first.`}
                            </p>
                        </header>
                        <div className="ai-usage__content">
                            <Reports {...props} />
                        </div>
                    </>
                )}
            </main>
        </div>
    );
};
